import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Skeleton,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Wrapper } from "../../components";
import { companyDashboardRecentApiCallData, apiAnalyticsGrouped } from "../../services";
import { formatDuration, formatTimestamp } from "../../utils/constant";

const CONTROL_HEIGHT = 38;

const FilterDropdown = ({ label, selectLabel, options, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const toggle = (value) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FilterAltOutlinedIcon sx={{ fontSize: 18 }} />}
        endIcon={open ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          height: CONTROL_HEIGHT,
          textTransform: "none",
          borderRadius: 3,
          px: 2,
          fontSize: "14px",
          color: "#0f172a",
          borderColor: selected.length > 0 ? "#2563EB" : "#e2e8f0",
          backgroundColor: "#fff",
        }}
      >
        {label}

      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 3, mt: 1, minWidth: 220, p: 1 } }}
      >
        <Typography sx={{ px: 1, pt: 0.5, pb: 1, fontSize: "14px", fontWeight: 600 }}>
          {selectLabel}
        </Typography>
        {options.map((opt) => (
          <Box
            key={opt}
            onClick={() => toggle(opt)}
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1,
              py: 0.5,
              cursor: "pointer",
              borderRadius: 1,
              "&:hover": { bgcolor: "#F8FAFC" },
            }}
          >
            <Checkbox
              checked={selected.includes(opt)}
              size="small"
              sx={{ p: 0, mr: 1 }}
            />
            <Typography sx={{ fontSize: "14px" }}>{String(opt)}</Typography>
          </Box>
        ))}
      </Popover>
    </>
  );
};

const SkeletonRows = ({ rows = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRow key={i}>
        <TableCell><Skeleton width={120} /></TableCell>
        <TableCell><Skeleton width={100} /></TableCell>
        <TableCell><Skeleton width={140} /></TableCell>
        <TableCell><Skeleton width={60} /></TableCell>
        <TableCell><Skeleton width={80} /></TableCell>
        <TableCell><Skeleton width={60} /></TableCell>
      </TableRow>
    ))}
  </>
);

export function ViewLogs() {
  const navigate = useNavigate();
  const { companyName } = useParams();
  const [searchParams] = useSearchParams();
  const apiName = searchParams.get("apiName");
  const date = searchParams.get("date");
  const startDateParam = searchParams.get("startDate");
  const endDateParam = searchParams.get("endDate");
  const decodedCompanyName = companyName ? decodeURIComponent(companyName) : null;
  const { user } = useSelector((state) => state.auth);
  const jwtToken = user?.jwt;

  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [selectedApiTypes, setSelectedApiTypes] = useState(apiName ? [apiName] : []);
  const [selectedStatusCodes, setSelectedStatusCodes] = useState([]);
  const [allApiNames, setAllApiNames] = useState([]);
  const [isMultiApiFetch, setIsMultiApiFetch] = useState(false);

  const apiTypeOptions = allApiNames.length > 0
    ? allApiNames
    : [...new Set(logs.map((l) => l.apiName).filter(Boolean))];

  const statusCodeOptions = useMemo(
    () => [...new Set(logs.map((l) => l.statusCode).filter((v) => v != null))].sort(),
    [logs]
  );

  const filteredAndPaginatedLogs = useMemo(() => {
    const filtered = selectedStatusCodes.length > 0
      ? logs.filter((row) => selectedStatusCodes.includes(row.statusCode))
      : logs;
    if (isMultiApiFetch) {
      const start = (page - 1) * rowsPerPage;
      return { displayLogs: filtered.slice(start, start + rowsPerPage), total: filtered.length };
    }
    return { displayLogs: filtered, total: totalCount };
  }, [logs, selectedStatusCodes, isMultiApiFetch, page, rowsPerPage, totalCount]);

  useEffect(() => {
    if (!jwtToken || !decodedCompanyName) return;

    const fetchLogs = async () => {
      try {
        setLoading(true);

        const dashboardDates = date
          ? { startDate: date, endDate: date }
          : startDateParam && endDateParam
            ? { startDate: startDateParam, endDate: endDateParam }
            : (() => {
                const now = new Date();
                const past = new Date();
                past.setDate(now.getDate() - 7);
                return {
                  startDate: past.toISOString().split("T")[0],
                  endDate: now.toISOString().split("T")[0],
                };
              })();

        if (selectedApiTypes.length <= 1) {
          setIsMultiApiFetch(false);
          const res = await companyDashboardRecentApiCallData(
            jwtToken,
            decodedCompanyName,
            dashboardDates,
            page,
            rowsPerPage,
            selectedApiTypes[0] || null
          );
          if (res?.status === 200) {
            setLogs(res.data?.apiCalls || []);
            setTotalCount(res.data?.pagination?.totalRecords || 0);
          }
        } else {
          setIsMultiApiFetch(true);
          const results = await Promise.all(
            selectedApiTypes.map((name) =>
              companyDashboardRecentApiCallData(jwtToken, decodedCompanyName, dashboardDates, 1, 1000, name)
            )
          );
          const allLogs = results.flatMap((res) => (res?.status === 200 ? res.data?.apiCalls || [] : []));
          allLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          setLogs(allLogs);
          setTotalCount(allLogs.length);
        }
      } catch (error) {
        console.error("Failed to fetch API logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [jwtToken, decodedCompanyName, date, startDateParam, endDateParam, selectedApiTypes, page, rowsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [selectedApiTypes, selectedStatusCodes]);

  useEffect(() => {
    if (!jwtToken || !decodedCompanyName) return;

    const dashboardDates = date
      ? { startDate: date, endDate: date }
      : startDateParam && endDateParam
        ? { startDate: startDateParam, endDate: endDateParam }
        : (() => {
            const now = new Date();
            const past = new Date();
            past.setDate(now.getDate() - 7);
            return {
              startDate: past.toISOString().split("T")[0],
              endDate: now.toISOString().split("T")[0],
            };
          })();

    apiAnalyticsGrouped(jwtToken, decodedCompanyName, dashboardDates.startDate, dashboardDates.endDate, null, 1, 1000)
      .then((res) => {
        if (res?.status === 200) {
          const logs = res.data?.logs || res.data?.data || res.data || [];
          setAllApiNames([...new Set(logs.map((l) => l.apiName).filter(Boolean))]);
        }
      })
      .catch(() => {});
  }, [jwtToken, decodedCompanyName, date, startDateParam, endDateParam]);

  const headerSx = { fontSize: "14px", fontWeight: 600, color: "#64748B", textAlign: "center" };
  const cellSx = { fontSize: "14px", textAlign: "center" };

  return (
    <Wrapper>
      <Stack sx={{ pt: 2, mt: 7 }} spacing={2} className="wrapper">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box
            onClick={() => navigate(-1)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 22, color: "#475569" }} />
            <Typography variant="h4" fontWeight="bold">
              Access Logs
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <FilterDropdown
              label={selectedApiTypes.length === 1 ? selectedApiTypes[0] : "API Type"}
              selectLabel="Select API Types"
              options={apiTypeOptions}
              selected={selectedApiTypes}
              onChange={setSelectedApiTypes}
            />
            <FilterDropdown
              label={selectedStatusCodes.length === 1 ? String(selectedStatusCodes[0]) : "Status Code"}
              selectLabel="Select Status Codes"
              options={statusCodeOptions}
              selected={selectedStatusCodes}
              onChange={setSelectedStatusCodes}
            />
          </Box>
        </Box>

        <Paper sx={{ width: "100%", borderRadius: 3, boxShadow: 2, p: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerSx}>API Name</TableCell>
                  <TableCell sx={headerSx}>Category</TableCell>
                  <TableCell sx={headerSx}>Timestamp</TableCell>
                  <TableCell sx={headerSx}>Status</TableCell>
                  <TableCell sx={headerSx}>Response Time</TableCell>
                  <TableCell sx={headerSx}>Credits</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonRows rows={rowsPerPage} />
                ) : filteredAndPaginatedLogs.displayLogs.length > 0 ? (
                  filteredAndPaginatedLogs.displayLogs.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell sx={cellSx}>{row.apiName}</TableCell>
                      <TableCell sx={cellSx}>{row.category}</TableCell>
                      <TableCell sx={cellSx}>{formatTimestamp(row.timestamp)}</TableCell>
                      <TableCell sx={cellSx}>
                        <Chip
                          label={row.statusCode}
                          size="small"
                          sx={{
                            bgcolor: row.statusCode === 200 ? "#e6f4ea" : "#fdecea",
                            color: row.statusCode === 200 ? "#2e7d32" : "#d32f2f",
                            fontSize: "13px",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={cellSx}>{formatDuration(row.responseTime)}</TableCell>
                      <TableCell sx={cellSx}>{row.creditsConsumed}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, fontSize: "15px" }}>
                      No logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredAndPaginatedLogs.total}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
            rowsPerPageOptions={[15, 25, 50, 100]}
            sx={{
              mt: 2,
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "12px",
                fontWeight: 600,
              },
              "& .MuiTablePagination-select": { fontSize: "12px" },
            }}
          />
        </Paper>
      </Stack>
    </Wrapper>
  );
}
